import React, { useEffect, useRef } from 'react'
import { Provider } from 'react-redux'
import { withTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { ReactKeycloakProvider } from '@react-keycloak/web'

import {
  initAxiosInstances,
  initSentry,
  loadGoogleMaps,
} from 'app/utils/initializer'
import store from 'app/store'
import AppNavigator from './AppNavigator'
import SentryFallback from 'app/components/reusable/SentryFallback'
import MediaProvider from 'app/context/ResponsiveUIContext'
import keycloak from '../utils/keycloak'
import { getCookies, setCookies } from 'app/utils/storage'
import AuthToken from 'app/models/Auth'

initAxiosInstances()
initSentry()

const AppRoutes = () => {
  const cookies = getCookies<AuthToken>()
  const hasValidTokens = cookies?.access_token && cookies?.refresh_token
  const tokensSetRef = useRef(false)

  useEffect(() => {
    loadGoogleMaps()
  }, [])

  // Force set tokens on keycloak instance BEFORE initialization
  useEffect(() => {
    if (hasValidTokens && !tokensSetRef.current) {
      console.log('Setting tokens directly on Keycloak instance')

      // Set tokens directly on the keycloak instance
      keycloak.token = cookies.access_token
      keycloak.refreshToken = cookies.refresh_token
      keycloak.idToken = cookies.id_token
      keycloak.authenticated = true

      // Also set the token parsed
      try {
        keycloak.tokenParsed = JSON.parse(
          atob(cookies.access_token.split('.')[1]),
        )
        keycloak.subject = keycloak.tokenParsed?.sub
        keycloak.realmAccess = keycloak.tokenParsed?.realm_access || {}
        keycloak.resourceAccess = keycloak.tokenParsed?.resource_access || {}
      } catch (error) {
        console.error('Error parsing token:', error)
      }

      tokensSetRef.current = true
    }
  }, [cookies, hasValidTokens])

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={{
        onLoad: hasValidTokens ? 'check-sso' : 'login-required',
        checkLoginIframe: false,
        pkceMethod: 'S256',
        // Remove token parameters from initOptions - they don't work properly
      }}
      onEvent={(event, error) => {
        console.log('Keycloak event:', event, error)

        if (event === 'onReady') {
          console.log('Keycloak ready - manual authentication check:', {
            authenticated: keycloak.authenticated,
            token: keycloak.token ? 'Present' : 'Missing',
            hasValidTokens,
          })

          // If we have tokens but keycloak doesn't think we're authenticated, force it
          if (hasValidTokens && !keycloak.authenticated) {
            console.log('Forcing authentication state')
            keycloak.authenticated = true
            keycloak.token = cookies.access_token
            keycloak.refreshToken = cookies.refresh_token
          }
        }

        if (event === 'onAuthError') {
          console.error('Auth error - but we have tokens, ignoring error')
          // If we have valid tokens, ignore auth errors
          if (hasValidTokens) {
            keycloak.authenticated = true
            keycloak.token = cookies.access_token
            keycloak.refreshToken = cookies.refresh_token
          }
        }
      }}
      onTokens={tokens => {
        console.log('Tokens received from Keycloak')
        // Only update cookies if we actually get new tokens
        if (tokens.token && tokens.token !== cookies?.access_token) {
          console.log('Storing new tokens in cookies')
          setCookies({
            access_token: tokens.token,
            refresh_token: tokens.refreshToken!,
            id_token: tokens.idToken!,
          })
        } else {
          console.log('Tokens unchanged or empty, skipping cookie update')
        }
      }}
      isLoadingCheck={keycloak => {
        // Custom loading check - consider initialized if we have tokens
        if (hasValidTokens) {
          console.log('Bypassing loading - we have tokens')
          return false
        }
        return !keycloak.authenticated
      }}
    >
      <Sentry.ErrorBoundary fallback={<SentryFallback />}>
        <Provider store={store}>
          <MediaProvider>
            <AppNavigator />
          </MediaProvider>
        </Provider>
      </Sentry.ErrorBoundary>
    </ReactKeycloakProvider>
  )
}

export default withTranslation()(AppRoutes)
